import { FormEventHandler, ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Autocomplete from '@mui/material/Autocomplete';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useAssignStore } from '@libs/store/talents';
import { Box, Flex, PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { TextField } from '@atoms/inputs';
import { FaIcon } from '@atoms/icons';

type AssignToMultipleShiftsDialogProps = Omit<PrimaryDialogProps, 'onSubmit'> & {
  shiftsNumber: number;
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  locationId?: string;
  qualifications?: string[];
  warningMessage?: ReactNode;
};
export function AssignToMultipleShiftsDialog({
  shiftsNumber,
  onSubmit,
  loading,
  locationId,
  qualifications,
  warningMessage,
  ...props
}: AssignToMultipleShiftsDialogProps) {
  return (
    <PrimaryDialog fullWidth maxWidth={'xs'} {...props}>
      <Content
        shiftsNumber={shiftsNumber}
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        locationId={locationId}
        qualifications={qualifications}
        warningMessage={warningMessage}
      />
    </PrimaryDialog>
  );
}

type ContentProps = {
  shiftsNumber: number;
  onCloseClick: () => void;
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  locationId?: string;
  qualifications?: string[];
  warningMessage?: ReactNode;
};
function Content({
  shiftsNumber,
  onCloseClick,
  onSubmit,
  loading = false,
  locationId,
  qualifications,
  warningMessage,
}: ContentProps) {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const [assigneeValue, setAssigneeValue] = useState<string>('');
  const debouncedValue = useDebounce(assigneeValue, 300);
  const [selected, setSelected] = useState<{
    label: string;
    richLabel: JSX.Element;
    value: string;
  } | null>();
  const loadTalents = useAssignStore((s) => s.loadData);
  const loadingTalents = useAssignStore((s) => s.loading);
  const talents = useAssignStore((s) => s.data);
  const options = useMemo(() => {
    return talents.map((talent) => {
      const name = [talent.firstName, talent.lastName].filter(Boolean).join(' ');
      return {
        label: name,
        richLabel: (
          <>
            <Flex justifyContent="space-between" alignCenter width="100%">
              <Typography>{name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(talent.qualifications || []).map((q) => q.abbreviation).join(', ')}
              </Typography>
            </Flex>
          </>
        ),
        value: talent.id,
      };
    });
  }, [talents]);
  useEffect(() => {
    if (locationId) {
      loadTalents({ search: debouncedValue, location: locationId, qualifications });
    }
  }, [loadTalents, locationId, debouncedValue, qualifications]);

  const handleAssigneeChange: FormEventHandler<HTMLDivElement> = (evt) => {
    const target = evt.target as HTMLInputElement;
    setAssigneeValue(target.value);
  };

  return (
    <>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Flex justifyContent="space-between">
          <Flex column gap={1}>
            <Typography variant="h5">
              <FormattedMessage id="shifts.recurrent.assignDialog.title" />
            </Typography>
          </Flex>
          <Box>
            <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
              <FaIcon name="xmark" variant="light" />
            </IconButton>
          </Box>
        </Flex>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" noWrap>
          <FormattedMessage id="shifts.recurrent.assignDialog.shiftNumber" values={{ number: shiftsNumber }} />
        </Typography>
        <Flex mt={2}>
          <Autocomplete
            fullWidth
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={<FormattedMessage id="shifts.recurrent.assignDialog.selectLabel" />}
                placeholder={intl.formatMessage({ id: 'shifts.recurrent.assignDialog.selectLabel' })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTalents ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={loadingTalents}
            onInput={handleAssigneeChange}
            onChange={(_, value) => {
              setSelected(value);
            }}
            value={selected}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.richLabel}
              </li>
            )}
          />
        </Flex>

        {warningMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {warningMessage}
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large">
            <FormattedMessage id="common.cancel" />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          variant="contained"
          rounded
          type="submit"
          size="large"
          onClick={() => selected && onSubmit?.(selected.value)}
        >
          <FormattedMessage id="common.submit" />
        </LoadingButton>
      </DialogActions>
    </>
  );
}
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
